/**
 * Content loader and integrity checker.
 *
 * Two jobs:
 *  1. Validate each file against its schema (shape).
 *  2. Validate references ACROSS files (meaning). A room pointing at a
 *     non-existent encounter, or a choice granting an item nobody defined, is a
 *     content bug that must fail the build rather than surface as a broken run.
 */

import { Dungeon } from '@lastroll/engine';
import type {
  CardDefRef,
  ContentBundle,
  DialogueFragment,
  DieDefinition,
  DungeonDef,
  EasterEggDef,
  EncounterDef,
  MonsterDef,
  NpcSeed,
} from '@lastroll/engine';
import {
  contentFileSchema,
  ID_PATTERN,
  type CardDef,
  type ClassDef,
  type ContentFile,
  type DialogueSet,
  type DungeonData,
  type EncounterData,
  type FactDef,
  type ItemDef,
  type MonsterData,
  type NpcDef,
} from './schema.js';

export interface ContentDb {
  readonly classes: readonly ClassDef[];
  readonly npcs: readonly NpcDef[];
  readonly dialogue: readonly DialogueSet[];
  readonly monsters: readonly MonsterData[];
  readonly dungeons: readonly DungeonData[];
  readonly encounters: readonly EncounterData[];
  readonly cards: readonly CardDef[];
  readonly items: readonly ItemDef[];
  readonly dice: readonly DieDefinition[];
  readonly easterEggs: readonly EasterEggDef[];
  readonly facts: readonly FactDef[];
}

/** Slots an NPC must be able to fill in any state, or they go mute. */
const REQUIRED_DIALOGUE_SLOTS = ['greet', 'body'] as const;

export interface ValidationIssue {
  readonly severity: 'error' | 'warning';
  readonly where: string;
  readonly message: string;
}

export class ContentValidationError extends Error {
  constructor(readonly issues: readonly ValidationIssue[]) {
    const errors = issues.filter((i) => i.severity === 'error');
    super(
      `Content validation failed with ${errors.length} error(s):\n` +
        errors.map((i) => `  [${i.where}] ${i.message}`).join('\n'),
    );
    this.name = 'ContentValidationError';
  }
}

/** Merge raw content files into one db, validating each against its schema. */
export function parseContentFiles(files: Record<string, unknown>): {
  db: ContentDb;
  issues: ValidationIssue[];
} {
  const issues: ValidationIssue[] = [];
  const merged: Required<{ [K in keyof ContentFile]: NonNullable<ContentFile[K]> }> = {
    classes: [],
    npcs: [],
    dialogue: [],
    monsters: [],
    dungeons: [],
    encounters: [],
    cards: [],
    items: [],
    dice: [],
    easterEggs: [],
    facts: [],
  };

  for (const [name, raw] of Object.entries(files)) {
    const parsed = contentFileSchema.safeParse(raw);
    if (!parsed.success) {
      for (const err of parsed.error.errors) {
        issues.push({
          severity: 'error',
          where: `${name}:${err.path.join('.')}`,
          message: err.message,
        });
      }
      continue;
    }
    for (const key of Object.keys(merged) as (keyof typeof merged)[]) {
      const entries = parsed.data[key];
      if (entries) (merged[key] as unknown[]).push(...entries);
    }
  }

  const db: ContentDb = {
    classes: merged.classes,
    npcs: merged.npcs,
    dialogue: merged.dialogue,
    monsters: merged.monsters,
    dungeons: merged.dungeons,
    encounters: merged.encounters,
    cards: merged.cards,
    items: merged.items,
    dice: merged.dice as readonly DieDefinition[],
    easterEggs: merged.easterEggs as readonly EasterEggDef[],
    facts: merged.facts,
  };

  issues.push(...checkReferences(db));
  return { db, issues };
}

/** Cross-file reference and uniqueness checks. */
export function checkReferences(db: ContentDb): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const err = (where: string, message: string): void =>
    void issues.push({ severity: 'error', where, message });
  const warn = (where: string, message: string): void =>
    void issues.push({ severity: 'warning', where, message });

  const dupCheck = <T extends { id: string }>(list: readonly T[], label: string): Set<string> => {
    const seen = new Set<string>();
    for (const entry of list) {
      if (seen.has(entry.id)) err(label, `duplicate id '${entry.id}'`);
      seen.add(entry.id);
    }
    return seen;
  };

  const itemIds = dupCheck(db.items, 'items');
  const cardIds = dupCheck(db.cards, 'cards');
  const npcIds = dupCheck(db.npcs, 'npcs');
  const monsterIds = dupCheck(db.monsters, 'monsters');
  const encounterIds = dupCheck(db.encounters, 'encounters');
  const factIds = dupCheck(db.facts, 'facts');
  const diceIds = dupCheck(db.dice as readonly { id: string }[], 'dice');
  dupCheck(db.easterEggs as readonly { id: string }[], 'easterEggs');

  const roomIds = new Set<string>();
  for (const dungeon of db.dungeons) {
    for (const room of dungeon.rooms) {
      if (roomIds.has(room.id)) err(`dungeon.${dungeon.id}`, `duplicate room id '${room.id}'`);
      roomIds.add(room.id);
    }
  }

  if (diceIds.size === 0) err('dice', 'at least one die must be defined');
  if (db.classes.length === 0) err('classes', 'at least one class must be defined');

  // --- dungeon integrity -------------------------------------------------
  for (const dungeon of db.dungeons) {
    const local = new Set(dungeon.rooms.map((r) => r.id));
    if (!local.has(dungeon.entrance)) {
      err(`dungeon.${dungeon.id}`, `entrance '${dungeon.entrance}' is not a room in this dungeon`);
    }
    if (!local.has(dungeon.bossRoom)) {
      err(`dungeon.${dungeon.id}`, `bossRoom '${dungeon.bossRoom}' is not a room in this dungeon`);
    }
    for (const room of dungeon.rooms) {
      for (const edge of room.edges) {
        if (!local.has(edge.to)) err(`room.${room.id}`, `edge points at unknown room '${edge.to}'`);
      }
      if (room.encounterId && !encounterIds.has(room.encounterId)) {
        err(`room.${room.id}`, `unknown encounterId '${room.encounterId}'`);
      }
      for (const mid of room.monsterIds ?? []) {
        if (!monsterIds.has(mid)) err(`room.${room.id}`, `unknown monsterId '${mid}'`);
      }
      for (const nid of room.npcIds ?? []) {
        if (!npcIds.has(nid)) err(`room.${room.id}`, `unknown npcId '${nid}'`);
      }
    }
    // Every room must be reachable from the entrance, or it is unplayable content.
    const reached = new Set<string>([dungeon.entrance]);
    const queue = [dungeon.entrance];
    while (queue.length > 0) {
      const cur = queue.shift()!;
      const room = dungeon.rooms.find((r) => r.id === cur);
      for (const edge of room?.edges ?? []) {
        if (!reached.has(edge.to)) {
          reached.add(edge.to);
          queue.push(edge.to);
        }
      }
    }
    for (const room of dungeon.rooms) {
      if (!reached.has(room.id)) {
        err(`room.${room.id}`, `unreachable from entrance '${dungeon.entrance}'`);
      }
    }
    if (!reached.has(dungeon.bossRoom)) {
      err(`dungeon.${dungeon.id}`, 'boss room is not reachable from the entrance');
    }
  }

  // --- encounter integrity ----------------------------------------------
  for (const enc of db.encounters) {
    if (enc.npcId && !npcIds.has(enc.npcId)) err(`encounter.${enc.id}`, `unknown npcId '${enc.npcId}'`);
    if (enc.monsterId && !monsterIds.has(enc.monsterId)) {
      err(`encounter.${enc.id}`, `unknown monsterId '${enc.monsterId}'`);
    }
    const choiceIds = new Set<string>();
    for (const choice of enc.choices) {
      if (choiceIds.has(choice.id)) err(`encounter.${enc.id}`, `duplicate choice id '${choice.id}'`);
      choiceIds.add(choice.id);
      if (choice.requiresItem && !itemIds.has(choice.requiresItem)) {
        err(`encounter.${enc.id}.${choice.id}`, `requiresItem '${choice.requiresItem}' is not defined`);
      }
      if (choice.requiresFact && !factIds.has(choice.requiresFact)) {
        err(`encounter.${enc.id}.${choice.id}`, `requiresFact '${choice.requiresFact}' is not defined`);
      }
      const outcomes = choice.roll ? Object.values(choice.roll.outcomes) : choice.outcome ? [choice.outcome] : [];
      for (const outcome of outcomes) {
        if (outcome.goto && !roomIds.has(outcome.goto)) {
          err(`encounter.${enc.id}.${choice.id}`, `goto references unknown room '${outcome.goto}'`);
        }
        for (const eff of outcome.effects) {
          checkEffectRefs(eff, `encounter.${enc.id}.${choice.id}`, { itemIds, cardIds, factIds, npcIds, monsterIds }, err, warn);
        }
      }
    }
    for (const eff of enc.onEnter ?? []) {
      checkEffectRefs(eff, `encounter.${enc.id}.onEnter`, { itemIds, cardIds, factIds, npcIds, monsterIds }, err, warn);
    }
  }

  // --- dialogue integrity -----------------------------------------------
  const dialogueNpcs = new Set<string>();
  for (const set of db.dialogue) {
    if (!npcIds.has(set.npcId)) err('dialogue', `dialogue for unknown npc '${set.npcId}'`);
    if (dialogueNpcs.has(set.npcId)) err('dialogue', `duplicate dialogue set for '${set.npcId}'`);
    dialogueNpcs.add(set.npcId);
    // Every fragment slot needs an unconditional fallback, or an NPC can fall
    // silent in a state nobody anticipated.
    const bySlot = new Map<string, boolean>();
    for (const frag of set.fragments) {
      const slot = frag.slot ?? 'body';
      bySlot.set(slot, (bySlot.get(slot) ?? false) || frag.when === undefined);
    }
    // Only the load-bearing slots need a fallback. Optional flourish slots
    // (an extra parting line at high trust) are meant to stay silent by default.
    for (const slot of REQUIRED_DIALOGUE_SLOTS) {
      if (!bySlot.has(slot)) {
        warn(`dialogue.${set.npcId}`, `has no '${slot}' fragments at all`);
      } else if (!bySlot.get(slot)) {
        warn(
          `dialogue.${set.npcId}`,
          `slot '${slot}' has no unconditional fallback, so the NPC can fall silent`,
        );
      }
    }
  }
  for (const npc of db.npcs) {
    if (!dialogueNpcs.has(npc.id)) warn('dialogue', `npc '${npc.id}' has no dialogue set`);
    for (const f of [...npc.knownFacts, ...npc.concealedFacts]) {
      if (!factIds.has(f)) err(`npc.${npc.id}`, `references unknown fact '${f}'`);
    }
  }

  // --- monster / class / card integrity ---------------------------------
  for (const mon of db.monsters) {
    for (const loot of mon.lootTable ?? []) {
      if (!itemIds.has(loot.itemId)) err(`monster.${mon.id}`, `loot references unknown item '${loot.itemId}'`);
    }
    if (mon.tier === 'boss' && (mon.phases ?? 1) < 2) {
      warn(`monster.${mon.id}`, 'boss has a single phase; GDD §7 expects bosses to evolve mid-fight');
    }
  }
  for (const cls of db.classes) {
    for (const itemId of cls.startingItems) {
      if (!itemIds.has(itemId)) err(`class.${cls.id}`, `startingItem '${itemId}' is not defined`);
    }
  }
  for (const card of db.cards) {
    for (const eff of card.effects) {
      checkEffectRefs(eff, `card.${card.id}`, { itemIds, cardIds, factIds, npcIds, monsterIds }, err, warn);
    }
    if (card.play) {
      const p = card.play;
      const doesSomething =
        p.setFace !== undefined ||
        p.faceDelta !== undefined ||
        p.bankFace === true ||
        p.movementBonus !== undefined ||
        p.fateDebt !== undefined ||
        p.hp !== undefined ||
        p.revealSecret !== undefined ||
        (p.effects?.length ?? 0) > 0;
      if (!doesSomething) {
        err(`card.${card.id}`, 'has a play block that does nothing');
      }
      for (const eff of p.effects ?? []) {
        checkEffectRefs(eff, `card.${card.id}.play`, { itemIds, cardIds, factIds, npcIds, monsterIds }, err, warn);
      }
    }
    for (const v of card.variants ?? []) {
      if (!/^[a-zA-Z0-9]+$/.test(v.overlay)) {
        err(`card.${card.id}`, `variant overlay '${v.overlay}' must be alphanumeric`);
      }
    }
  }

  // --- reachability of content -------------------------------------------
  // Content nobody can encounter still costs art, audio and review budget, so
  // surface it rather than letting it sit unnoticed.
  const placedMonsters = new Set<string>();
  const placedEncounters = new Set<string>();
  for (const dungeon of db.dungeons) {
    for (const room of dungeon.rooms) {
      for (const mid of room.monsterIds ?? []) placedMonsters.add(mid);
      if (room.encounterId) placedEncounters.add(room.encounterId);
    }
  }
  for (const enc of db.encounters) {
    if (enc.monsterId) placedMonsters.add(enc.monsterId);
    if (!placedEncounters.has(enc.id)) {
      warn(`encounter.${enc.id}`, 'not referenced by any room; unreachable content');
    }
  }
  for (const mon of db.monsters) {
    if (!placedMonsters.has(mon.id)) {
      warn(`monster.${mon.id}`, 'not placed in any room or encounter; unreachable content');
    }
  }

  // --- id convention -----------------------------------------------------
  const allIds: [string, Iterable<string>][] = [
    ['item', itemIds],
    ['card', cardIds],
    ['npc', npcIds],
    ['encounter', encounterIds],
    ['room', roomIds],
    ['fact', factIds],
    ['die', diceIds],
  ];
  for (const [label, ids] of allIds) {
    for (const id of ids) {
      if (!ID_PATTERN.test(id)) err(label, `id '${id}' violates the ID pattern (docs/04 §3)`);
    }
  }

  return issues;
}

function checkEffectRefs(
  eff: { type: string; target?: string; data?: Record<string, unknown>; setFlag?: string },
  where: string,
  sets: {
    itemIds: Set<string>;
    cardIds: Set<string>;
    factIds: Set<string>;
    npcIds: Set<string>;
    monsterIds: Set<string>;
  },
  err: (w: string, m: string) => void,
  _warn: (w: string, m: string) => void,
): void {
  const d = eff.data ?? {};
  const asStr = (v: unknown): string | null => (typeof v === 'string' ? v : null);

  const itemId = asStr(d['itemId']);
  if (itemId && !sets.itemIds.has(itemId)) err(where, `effect references unknown item '${itemId}'`);

  const factId = asStr(d['factId']);
  if (factId && !sets.factIds.has(factId)) err(where, `effect references unknown fact '${factId}'`);

  for (const key of ['cardId', 'from', 'to'] as const) {
    const raw = asStr(d[key]);
    if (!raw) continue;
    // Transformed ids are `base+overlay`; validate the base.
    const base = raw.split('+')[0]!;
    if (!sets.cardIds.has(base)) err(where, `effect references unknown card '${raw}'`);
  }

  if (eff.target && eff.type.startsWith('NPC_') && !sets.npcIds.has(eff.target)) {
    err(where, `effect targets unknown npc '${eff.target}'`);
  }
  if (
    eff.target &&
    (eff.type.startsWith('BOSS_') || eff.type.startsWith('MONSTER_') || eff.type === 'COMBAT_STARTED') &&
    !sets.monsterIds.has(eff.target)
  ) {
    err(where, `effect targets unknown monster '${eff.target}'`);
  }
}

/** Build the runtime bundle the engine consumes. Throws on any error-level issue. */
export function buildBundle(db: ContentDb, dungeonId?: string): ContentBundle {
  const dungeonData = dungeonId
    ? db.dungeons.find((d) => d.id === dungeonId)
    : db.dungeons[0];
  if (!dungeonData) throw new Error(`Dungeon '${dungeonId ?? '<first>'}' not found in content`);

  const monsters = new Map<string, MonsterDef>(
    db.monsters.map((m) => [m.id, m as unknown as MonsterDef]),
  );
  const encounters = new Map<string, EncounterDef>(
    db.encounters.map((e) => [e.id, e as unknown as EncounterDef]),
  );
  const dialogue = new Map<string, readonly DialogueFragment[]>(
    db.dialogue.map((d) => [d.npcId, d.fragments as readonly DialogueFragment[]]),
  );
  const npcNames = new Map<string, string>(db.npcs.map((n) => [n.id, n.name]));
  const easterEggs = new Map<string, EasterEggDef>(db.easterEggs.map((e) => [e.id, e]));

  const startingItems: Partial<Record<ClassDef['id'], readonly string[]>> = {};
  for (const cls of db.classes) startingItems[cls.id] = cls.startingItems;

  const cards = new Map<string, CardDefRef>(
    db.cards.map((c) => [c.id, c as unknown as CardDefRef]),
  );

  // The vertical-slice deck is the cards a player can actually DO something with,
  // plus the EVENT cards the story transforms. A deck full of unplayable records
  // would just clog the hand. Deck building arrives in GOD-2.
  const startingDeck = db.cards
    .filter((c) => !c.deprecated && (c.play !== undefined || c.type === 'EVENT'))
    .map((c) => c.id);

  // Seeds let the engine answer `knows:`/`conceals:` conditions without the
  // reducer having to carry authored NPC knowledge around in every save.
  const npcSeeds = new Map<string, NpcSeed>(
    db.npcs.map((n) => [n.id, { knownFacts: n.knownFacts, concealedFacts: n.concealedFacts }]),
  );

  return {
    dungeon: new Dungeon(dungeonData as unknown as DungeonDef),
    monsters,
    encounters,
    dialogue,
    npcNames,
    npcSeeds,
    cards,
    dice: db.dice,
    startingDeck,
    startingItems,
    easterEggs,
  };
}
